import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import './index.css';
import './styles/theme-overrides.css';
import App from './components/App';
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import NotFound from './components/NotFound';
import TopBar from './components/TopBar';
import Footer from './components/Footer';
import CommonModules from './components/CommonModules';
import reportWebVitals from './reportWebVitals';
import { IntlProvider } from 'react-intl';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { getSite, getTheme } from './services/CommonServices';
import { BuilderProvider } from './contexts/BuilderContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { InstanceProvider, useInstance } from './contexts/InstanceContext';
import SetupWizard from './components/SetupWizard';

import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';

// One-time migration: drop legacy localStorage 'selectedMemberId' now that
// memberId comes from the DB via InstanceContext. Without this, clients that
// cached an old env-sourced id would keep overriding the DB value.
if (typeof window !== 'undefined' && !localStorage.getItem('selectedMemberId_dbMigrated_v1')) {
  localStorage.removeItem('selectedMemberId');
  localStorage.setItem('selectedMemberId_dbMigrated_v1', '1');
}

function AppWithIntl({ site, theme, globalHeaderType, globalFooterType }) {
  const { locale, messages, isLoading } = useLanguage();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      <BrowserRouter>
        <TopBar />
        <CommonModules />
        <Switch>
          <Route
            exact
            path="/"
            render={(props) => (
              <App
                {...props}
                site={site}
                globalHeaderType={globalHeaderType}
                globalFooterType={globalFooterType}
              />
            )}
          />
          <Route
            exact
            path="/blog"
            render={(props) => (
              <BlogList
                {...props}
                site={site}
                globalHeaderType={globalHeaderType}
                globalFooterType={globalFooterType}
              />
            )}
          />
          <Route
            exact
            path="/blog/:slug"
            render={(props) => (
              <BlogPost
                {...props}
                site={site}
                globalHeaderType={globalHeaderType}
                globalFooterType={globalFooterType}
              />
            )}
          />
          <Route component={NotFound} />
        </Switch>
        <Footer />
      </BrowserRouter>
    </IntlProvider>
  );
}

function SiteLoader() {
  const { memberId, isConfigured, isLoading: instanceLoading } = useInstance();
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [theme, setTheme] = useState({ colors: {}, font: {} });

  useEffect(() => {
    // Wait for instance config check
    if (instanceLoading) return;

    // If not configured, don't load site
    if (!isConfigured || !memberId) {
      setLoading(false);
      return;
    }

    // Use memberId from instance config instead of env variable
    getSite(memberId)
      .then((siteRes) => {
        if (siteRes.data && siteRes.data.data) {
          setSite(siteRes.data.data);

          const themeId = siteRes.data.data.themeId;
          if (themeId) {
            getTheme(themeId)
              .then((themeRes) => {
                if (themeRes && themeRes.data && themeRes.data.data) {
                  setTheme({
                    colors: themeRes.data.data.colors || {},
                    font: themeRes.data.data.font || {},
                  });
                }
                setLoading(false);
              })
              .catch((err) => {
                console.error('Failed to load theme', err);
                setLoading(false);
              });
            return;
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [memberId, isConfigured, instanceLoading]);

  // Show loading while checking instance config
  if (instanceLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Checking configuration...
      </div>
    );
  }

  // Show setup wizard if not configured
  if (!isConfigured) {
    return <SetupWizard />;
  }

  // Show loading while fetching site data
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Loading...
      </div>
    );
  }

  const globalHeaderType = site?.globalHeaderType || 'siteSub.header-01';
  const globalFooterType = site?.globalFooterType || 'siteSub.footer-01';

  return (
    <LanguageProvider memberId={memberId}>
      <ThemeProvider siteId={site?.id} initialThemeId={site?.themeId}>
        <BuilderProvider>
          <StyledThemeProvider theme={theme}>
            <AppWithIntl
              site={site}
              theme={theme}
              globalHeaderType={globalHeaderType}
              globalFooterType={globalFooterType}
            />
          </StyledThemeProvider>
        </BuilderProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

function Root() {
  return (
    <InstanceProvider>
      <SiteLoader />
    </InstanceProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<Root />);

reportWebVitals();
